/**
 * ConfigGeneralPage
 *
 * Admin page for platform URL configuration (api_url, web_url, docs_url).
 * Stored in organisation/{org_id}/config/general in Firestore.
 *
 * Route: /settings/config/general  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";

const EMPTY_FORM = {
  api_url: "",
  web_url: "",
  docs_url: "",
};

const ConfigGeneralPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("general");
      if (data.configured) {
        const loaded = {
          api_url: data.api_url ?? "",
          web_url: data.web_url ?? "",
          docs_url: data.docs_url ?? "",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load general config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("general", form);
      setOriginalForm(form);
      showFeedback("General config saved and active.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save general config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="General Configuration"
      description="Platform URLs used for link construction in emails, OAuth callbacks, and API integrations."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">

          {/* API URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              API URL
            </label>
            <Input
              type="url"
              placeholder="https://api.example.com"
              value={form.api_url}
              onChange={set("api_url")}
            />
            <p className="text-[11px] text-ink-gray-400">
              Backend API base URL. Used for invite links and webhook registrations.
            </p>
          </div>

          {/* Web URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Web URL
            </label>
            <Input
              type="url"
              placeholder="https://app.example.com"
              value={form.web_url}
              onChange={set("web_url")}
            />
            <p className="text-[11px] text-ink-gray-400">
              Frontend app URL. Used in email templates, OAuth redirects, and auth callbacks.
            </p>
          </div>

          {/* Docs service URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Docs Service URL
            </label>
            <Input
              type="url"
              placeholder="https://photon-docs-example.a.run.app"
              value={form.docs_url}
              onChange={set("docs_url")}
            />
            <p className="text-[11px] text-ink-gray-400">
              Direct Cloud Run origin without /docs. Used for authenticated Photon Guide retrieval.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigGeneralPage;
